import { useEffect, useMemo, useState } from 'react';
import type { Account } from '../../core/accounts';
import { formatDateKey, todayKey } from '../../core/dates';
import { formatPaise } from '../../core/money';
import { TRASH_DAYS, describeRemaining, summarise, type Trashed } from '../../core/trash';
import type { Transaction } from '../../core/transactions';
import { watchAccounts } from '../../data/accounts';
import type { UserRecord } from '../../data/auth';
import { restoreTransaction } from '../../data/transactions';
import { loadTrashedTransactions, watchTrash } from '../../data/trash';

interface Props {
  record: UserRecord;
}

/**
 * Trash (FR-DEL-01, FR-DEL-04).
 *
 * A last chance, so the screen leads with what is about to be lost rather than with what
 * was deleted most recently. **Nothing here destroys anything** — the purge runs as a
 * scheduled job under the service account, and the client rules deny hard delete on every
 * collection, so this screen could not empty the bin even if it offered to.
 */
export function TrashScreen({ record }: Props) {
  const today = todayKey();

  const [items, setItems] = useState<Trashed[] | null>(null);
  const [full, setFull] = useState<Transaction[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => watchTrash(setItems), []);
  useEffect(() => watchAccounts(setAccounts), []);

  // The full records, for the restore path — a restore has to re-apply the balance and
  // rollup effects, which needs more than the trash row carries.
  useEffect(() => {
    let live = true;
    void loadTrashedTransactions().then((rows) => {
      if (live) setFull(rows);
    });
    return () => {
      live = false;
    };
  }, [items?.length]);

  const accountType = useMemo(() => {
    const map = new Map(accounts.map((a) => [a.id, a.type]));
    return (id: string) => map.get(id);
  }, [accounts]);

  const summary = useMemo(() => summarise(items ?? [], today), [items, today]);

  const ordered = useMemo(
    () =>
      [...(items ?? [])].sort(
        (a, b) => a.purgeAfter.localeCompare(b.purgeAfter) || a.label.localeCompare(b.label),
      ),
    [items],
  );

  async function restore(item: Trashed) {
    const transaction = full.find((t) => t.id === item.id);
    if (!transaction) {
      setError('That record could not be loaded. Reload and try again.');
      return;
    }

    setError(null);
    setBusy(item.id);
    try {
      // FR-DEL-04: restored in full, including the rollup and balance effects that were
      // reversed when it was deleted.
      await restoreTransaction(transaction, record.uid, accountType);
      setNotice(`Restored "${item.label}".`);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not restore that.');
    } finally {
      setBusy(null);
    }
  }

  if (items === null) return <p className="muted">Loading…</p>;

  return (
    <>
      <div className="head-row">
        <h1>Trash</h1>
        <span className="muted tiny">
          Deleted records are kept {TRASH_DAYS} days, then destroyed by the nightly job.
        </span>
      </div>

      {error && <p className="error">{error}</p>}
      {notice && <p className="ok">{notice}</p>}

      {items.length === 0 ? (
        <div className="placeholder">Nothing in the trash.</div>
      ) : (
        <>
          <div className="figures">
            <div className="figure">
              <div className="figure-label">In the trash</div>
              <div className="figure-value">{summary.total}</div>
              {summary.attachments > 0 && (
                <div className="muted tiny">
                  {summary.attachments} attachment{summary.attachments === 1 ? '' : 's'} go with them
                </div>
              )}
            </div>
            {summary.dueToday > 0 && (
              <div className="figure">
                <div className="figure-label">Due to be destroyed</div>
                <div className="figure-value warn">{summary.dueToday}</div>
                <div className="muted tiny">on the next nightly run</div>
              </div>
            )}
          </div>

          <div className="table-scroll">
            <table className="table reclassify-table">
              <thead>
                <tr>
                  <th>Deleted</th>
                  <th>Record</th>
                  <th className="num">Amount</th>
                  <th>Time left</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {ordered.map((item) => (
                  <tr key={item.id}>
                    <td>{item.deletedOn ? formatDateKey(item.deletedOn, 'dd MMM yyyy') : '—'}</td>
                    <td>
                      {item.label}
                      {item.attachments.length > 0 && (
                        <span className="badge">
                          {item.attachments.length} file{item.attachments.length === 1 ? '' : 's'}
                        </span>
                      )}
                    </td>
                    <td className="num">
                      {item.amountPaise === undefined
                        ? '—'
                        : formatPaise(item.amountPaise, { symbol: false })}
                    </td>
                    <td className={item.purgeAfter <= today ? 'warn' : undefined}>
                      {describeRemaining(item, today)}
                    </td>
                    <td className="actions">
                      <button
                        type="button"
                        className="btn"
                        disabled={busy !== null}
                        onClick={() => void restore(item)}
                      >
                        {busy === item.id ? 'Restoring…' : 'Restore'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="muted tiny">
            {/* FR-DEL-03, said where it matters rather than only in the requirements. */}
            When a record is destroyed its attachments go with it, but its audit entries
            stay — the record goes, the record of what happened to it does not.
          </p>
        </>
      )}
    </>
  );
}
